import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Star, User, MessageCircle } from "lucide-react";

export const SupplierRating = ({ supplierId, supplierName }) => {
  const { user, token } = useAuth();
  const [ratings, setRatings] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalRatings, setTotalRatings] = useState(0);
  const [myRating, setMyRating] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [review, setReview] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRatings();
    if (token) fetchMyRating();
  }, [supplierId, token]);

  const fetchRatings = async () => {
    try {
      const response = await axios.get(`${API}/suppliers/${supplierId}/ratings`);
      setRatings(response.data.ratings || []);
      setAverageRating(response.data.average_rating || 0);
      setTotalRatings(response.data.total_ratings || 0);
    } catch (error) {
      console.error("Failed to fetch ratings:", error);
    }
  };

  const fetchMyRating = async () => {
    try {
      const response = await axios.get(`${API}/suppliers/${supplierId}/my-rating`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setMyRating(response.data);
        setSelectedRating(response.data.rating);
        setReview(response.data.review || "");
      }
    } catch (error) {
      console.error("Failed to fetch my rating:", error);
    }
  };

  const submitRating = async () => {
    if (!token) {
      toast.error("कृपया पहले लॉगिन करें");
      return;
    }
    if (selectedRating === 0) {
      toast.error("कृपया स्टार रेटिंग चुनें");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/suppliers/${supplierId}/rate`,
        {
          supplier_id: supplierId,
          rating: selectedRating,
          review: review,
          review_hindi: review
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("रेटिंग सफलतापूर्वक दी गई!");
      setShowForm(false);
      fetchRatings();
      fetchMyRating();
    } catch (error) {
      toast.error(error.response?.data?.detail || "रेटिंग देने में त्रुटि");
    }
    setLoading(false);
  };

  const StarRating = ({ rating, interactive = false, size = "md" }) => {
    const sizeClass = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-8 h-8" : "w-6 h-6";
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && setSelectedRating(star)}
            className={`${interactive ? "cursor-pointer hover:scale-110" : "cursor-default"} transition-transform`}
            disabled={!interactive}
          >
            <Star
              className={`${sizeClass} ${
                star <= (interactive ? selectedRating : rating)
                  ? "fill-yellow-500 text-yellow-500"
                  : "text-slate-600"
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
      {/* Header with Average Rating */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-rajdhani font-bold text-xl text-gray-800 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            रेटिंग और रिव्यू
          </h3>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-3xl font-bold text-yellow-500">{averageRating.toFixed(1)}</span>
            <div>
              <StarRating rating={Math.round(averageRating)} size="sm" />
              <p className="text-xs text-gray-8000">{totalRatings} रिव्यू</p>
            </div>
          </div>
        </div>
        
        {user && (
          <Button
            onClick={() => setShowForm(!showForm)}
            className={myRating ? "btn-secondary rounded-sm" : "btn-primary rounded-sm"}
          >
            <Star className="w-4 h-4 mr-2" />
            {myRating ? "अपनी रेटिंग बदलें" : "रेटिंग दें"}
          </Button>
        )}
      </div>

      {/* Rating Form */}
      {showForm && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <p className="text-sm text-gray-500 mb-3">
            {supplierName} को रेटिंग दें
          </p>
          
          <div className="mb-4">
            <StarRating rating={selectedRating} interactive size="lg" />
            <p className="text-xs text-gray-8000 mt-1">
              {selectedRating === 0 ? "स्टार पर क्लिक करें" :
               selectedRating === 1 ? "बहुत खराब" :
               selectedRating === 2 ? "खराब" :
               selectedRating === 3 ? "ठीक है" :
               selectedRating === 4 ? "अच्छा" : "बहुत अच्छा"}
            </p>
          </div>
          
          <Textarea
            placeholder="अपना अनुभव बताएं (वैकल्पिक)..."
            value={review}
            onChange={(e) => setReview(e.target.value)}
            className="input-industrial mb-4 min-h-[80px]"
          />
          
          <div className="flex gap-3">
            <Button
              onClick={submitRating}
              disabled={loading || selectedRating === 0}
              className="btn-primary rounded-sm"
            >
              {loading ? "जमा हो रहा है..." : "रेटिंग जमा करें"}
            </Button>
            <Button
              onClick={() => setShowForm(false)}
              variant="outline"
              className="btn-secondary rounded-sm"
            >
              रद्द करें
            </Button>
          </div>
        </div>
      )}

      {/* Reviews List */}
      {ratings.length > 0 ? (
        <div className="space-y-4">
          {ratings.slice(0, 5).map((rating) => (
            <div
              key={rating.id}
              className="p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">{rating.user_name}</p>
                    <p className="text-xs text-gray-8000">
                      {new Date(rating.created_at).toLocaleDateString('hi-IN')}
                    </p>
                  </div>
                </div>
                <StarRating rating={rating.rating} size="sm" />
              </div>
              {rating.review && (
                <p className="text-gray-500 text-sm ml-13">
                  {rating.review_hindi || rating.review}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <MessageCircle className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-gray-8000">अभी तक कोई रिव्यू नहीं</p>
          <p className="text-xs text-slate-600">पहले रिव्यू देने वाले बनें!</p>
        </div>
      )}
    </div>
  );
};
